import { useState, useCallback, useRef } from 'react';

/**
 * 🎯 Hook para gerenciar microinterações das reações
 * 
 * Funcionalidades:
 * - Animação de bounce no clique
 * - Feedback visual imediato
 * - Prevenção de spam de cliques
 * - Gerenciamento de estado de animação
 * 
 * @returns {Object} Funções e estados para microinterações
 */
export const useReactionMicrointeractions = () => {
  const [animatingReactions, setAnimatingReactions] = useState(new Set());
  const [clickCooldown, setClickCooldown] = useState(new Set());
  const animationTimeouts = useRef(new Map());

  /**
   * 🎨 Adiciona animação de bounce a uma reação
   * @param {string} reactionId - ID único da reação (messageId + emoji)
   */
  const triggerBounceAnimation = useCallback((reactionId) => {
    // Previne spam de cliques
    if (clickCooldown.has(reactionId)) return;

    // Adiciona à lista de animações ativas
    setAnimatingReactions(prev => new Set([...prev, reactionId]));
    
    // Adiciona cooldown temporário
    setClickCooldown(prev => new Set([...prev, reactionId]));

    // Limpa timeout anterior se existir
    if (animationTimeouts.current.has(reactionId)) {
      clearTimeout(animationTimeouts.current.get(reactionId));
    }

    // Remove animação após completar
    const timeout = setTimeout(() => {
      setAnimatingReactions(prev => {
        const newSet = new Set(prev);
        newSet.delete(reactionId);
        return newSet;
      });
      
      setClickCooldown(prev => {
        const newSet = new Set(prev);
        newSet.delete(reactionId);
        return newSet;
      });
      
      animationTimeouts.current.delete(reactionId);
    }, 300); // Duração da animação de bounce

    animationTimeouts.current.set(reactionId, timeout);
  }, [clickCooldown]);

  /**
   * 🎯 Verifica se uma reação está animando
   * @param {string} reactionId - ID da reação
   * @returns {boolean}
   */
  const isReactionAnimating = useCallback((reactionId) => {
    return animatingReactions.has(reactionId);
  }, [animatingReactions]);

  /**
   * 🎯 Verifica se uma reação pode ser clicada (não está em cooldown)
   * @param {string} reactionId - ID da reação
   * @returns {boolean}
   */
  const canClickReaction = useCallback((reactionId) => {
    return !clickCooldown.has(reactionId);
  }, [clickCooldown]);

  /**
   * 🎨 Gerencia o clique em uma reação com microinteração
   * @param {string} messageId - ID da mensagem
   * @param {string} emoji - Emoji da reação
   * @param {Function} onReact - Função callback para processar a reação
   */
  const handleReactionClick = useCallback((messageId, emoji, onReact) => {
    const reactionId = `${messageId}-${emoji}`;
    
    // Previne spam
    if (!canClickReaction(reactionId)) return;

    // Dispara animação
    triggerBounceAnimation(reactionId);
    
    // Executa a ação da reação
    onReact(messageId, emoji);
  }, [canClickReaction, triggerBounceAnimation]);

  /**
   * 🎯 Gera classes CSS para animação de uma reação
   * @param {string} messageId - ID da mensagem
   * @param {string} emoji - Emoji da reação
   * @returns {string} Classes CSS
   */
  const getReactionAnimationClasses = useCallback((messageId, emoji) => {
    const reactionId = `${messageId}-${emoji}`;
    
    if (isReactionAnimating(reactionId)) {
      return 'reaction-bouncing';
    }
    
    return '';
  }, [isReactionAnimating]);

  /**
   * 🧹 Limpa todos os timeouts ao desmontar o componente
   */
  const cleanup = useCallback(() => {
    animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    animationTimeouts.current.clear();
    setAnimatingReactions(new Set());
    setClickCooldown(new Set());
  }, []);

  return {
    // Funções principais
    handleReactionClick,
    triggerBounceAnimation,
    getReactionAnimationClasses,
    cleanup,
    
    // Estados
    isReactionAnimating,
    canClickReaction,
    
    // Dados internos (para debug)
    animatingReactions: Array.from(animatingReactions),
    clickCooldown: Array.from(clickCooldown),
  };
};

export default useReactionMicrointeractions;
